import React from 'react';
import { useGate } from 'effector-react';
import {
  type EditConfigLoading,
  EditConfigLoadingType,
  ImageType,
} from '@types';
import useTourEditing from '@hooks/useTourEditing';
import Title from '@components/Title';
import { Collapse } from '@components/Collapse';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import ColorPicker from '@components/ColorPicker';
import ImageContainer from '@components/ImageContainer';
import Tip from '@components/Tip';
import Button from '@components/Button';
import { Validator } from '@components/Validator';
import Image from '@components/Image';
import { $GateConfigLoading } from '@stores/editingStores';
import { selectLibImages } from '../../mediaLib';
import type { PanelBaseProps } from '../types';
import PanelBase from '../PanelBase';

const typeItems: DropdownItem<EditConfigLoadingType>[] = [
  { id: EditConfigLoadingType.system, name: '系统默认' },
  { id: EditConfigLoadingType.text, name: '纯文字' },
  { id: EditConfigLoadingType.custom, name: '自定义' },
];

export function ConfigLoadingBase({
  defaultValue,
  onConfirm,
}: PanelBaseProps<EditConfigLoading>) {
  const { state, setState, defaultState, isChanged } = useTourEditing(
    defaultValue,
    {
      type: EditConfigLoadingType.system,
      mediaId: 0,
      mediaUrl: '',
      bgColor: '0,0,0,1',
      itemColor: '255,255,255,1',
    }
  );

  useGate($GateConfigLoading, state);

  const handleTypeChange = (typeId: EditConfigLoadingType) => {
    setState((draft) => ({
      ...defaultState,
      type: typeId,
      bgColor: draft.bgColor,
    }));
  };

  const renderBgcSetting = () => {
    return (
      <ColorPicker
        colorString={state?.bgColor}
        onChange={(str) =>
          setState((draft) => {
            draft.bgColor = str;
          })
        }
      >
        <ColorPicker.Header>
          <div>背景颜色</div>
          <ColorPicker.Reset defaultColor={defaultState.bgColor!} />
        </ColorPicker.Header>
        <ColorPicker.Bar disableAlpha />
      </ColorPicker>
    );
  };

  const renderColorSetting = (title: string) => {
    return (
      <Collapse title="背景设置">
        <ColorPicker
          className="mb-20"
          colorString={state?.itemColor}
          onChange={(str) =>
            setState((draft) => {
              draft.itemColor = str;
            })
          }
        >
          <ColorPicker.Header>
            <div>{title}</div>
            <ColorPicker.Reset defaultColor={defaultState.itemColor!} />
          </ColorPicker.Header>
          <ColorPicker.Bar disableAlpha />
        </ColorPicker>
        {renderBgcSetting()}
      </Collapse>
    );
  };

  const renderCustomSetting = () => {
    return (
      <>
        <Collapse title="图片设置">
          <Validator
            value={state?.mediaUrl}
            validFn={(value) => !!value}
            warningMessage="请选择图片"
          >
            {({ value, warning }) => (
              <ImageContainer
                warning={warning}
                className="h-115"
                placeholder={
                  <Tip className="text-center">
                    <p>请选择图片</p>
                    <p>支持JPG，PNG、GIF</p>
                  </Tip>
                }
                onDeleteClick={() => {
                  setState((draft) => {
                    draft.mediaUrl = '';
                    draft.mediaId = 0;
                  });
                }}
              >
                {!!value && (
                  <Image
                    className="max-h-full max-w-full"
                    type={ImageType.media}
                    src={value}
                  />
                )}
              </ImageContainer>
            )}
          </Validator>
          <div className="mt-10 flex items-center justify-between">
            <Tip>建议尺寸：50x50</Tip>
            <Button
              size="sm"
              onClick={async () => {
                const { libItems } = await selectLibImages();
                if (!libItems) return;
                setState((draft) => {
                  draft.mediaId = libItems[0].id;
                  draft.mediaUrl = libItems[0].url;
                });
              }}
            >
              选择图片
            </Button>
          </div>
        </Collapse>
        <Collapse title="背景设置">{renderBgcSetting()}</Collapse>
      </>
    );
  };

  return (
    <PanelBase
      isChanged={isChanged}
      onConfirm={() => onConfirm(state)}
      title={<Title>页面加载样式</Title>}
    >
      <Collapse title="加载样式设置">
        <Dropdown
          selected={state?.type}
          items={typeItems}
          onChange={handleTypeChange}
        />
      </Collapse>
      {state?.type === EditConfigLoadingType.system &&
        renderColorSetting('图标颜色')}
      {state?.type === EditConfigLoadingType.text &&
        renderColorSetting('文字颜色')}
      {state?.type === EditConfigLoadingType.custom && renderCustomSetting()}
    </PanelBase>
  );
}
